import { Button } from '@cloudscape-design/components';
import FileInput from '@cloudscape-design/components/file-input';
import { IconCheck, IconLoader } from '@tabler/icons-react';
import { remove, uploadData } from 'aws-amplify/storage';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';

import getCroppedImg from './cropImage';

type FileUploadProps = {
  changeImageUrl: (key?: string) => void;
  imageKey?: string;
};

const FileUpload = ({ changeImageUrl, imageKey }: FileUploadProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [easyFile, setEasyFile] = useState<string | null>(imageKey ?? null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(imageKey ? true : false);

  const handleFileChange = (files: File[]) => {
    if (files && files.length > 0) {
      changeImageUrl('');
      setFile(files[0]);
      const imageUrl = URL.createObjectURL(files[0]);
      setEasyFile(imageUrl);
      setUploading(false);
      setUploaded(false);
    }
  };
  const handleUpload = async () => {
    if (!easyFile) {
      alert('Please select a file first.');
      return;
    }
    setUploading(true);
    try {
      const result = await getCroppedImg(
        easyFile === imageKey
          ? `https://${process.env.REACT_APP_UPLOAD_BUCKET}.s3.eu-central-1.amazonaws.com/${imageKey}`
          : easyFile,
        croppedAreaPixels,
      );
      let newKey = '';
      //delete previous version
      if (imageKey) {
        await remove({
          path: imageKey,
        });
        newKey = `events/${DateTime.now().toUnixInteger()}_${imageKey.replace(/^[^_]*_/, '')}`;
      }
      let blob = await fetch(result).then((r) => r.blob());
      if (result) {
        const res = await uploadData({
          path:
            easyFile === imageKey
              ? newKey
              : `events/${DateTime.now().toUnixInteger()}_${file?.name}`,
          data: blob,
        }).result;
        if (res) {
          changeImageUrl(res.path);
          setZoom(1);
          setCrop({ x: 0, y: 0 });
          setEasyFile(
            `https://${process.env.REACT_APP_UPLOAD_BUCKET}.s3.eu-central-1.amazonaws.com/${res.path}`,
          );
          setUploading(false);
          setUploaded(true);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const onCropAreaChange = useCallback(
    (location: Point) => {
      if (location.x !== 0 || location.y !== 0) {
        setCrop({ x: location.x, y: location.y });
        setUploaded(false);
      } else {
        setCrop({ x: 0, y: 0 });
        if (imageKey) {
          setUploaded(true);
        }
      }
    },
    [imageKey],
  );

  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleDeleteImage = useCallback(() => {
    changeImageUrl('');
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setUploading(false);
    setUploaded(false);
    setEasyFile(null);
    setFile(null);
  }, [changeImageUrl]);

  return (
    <div>
      <div className="flex flex-1 flex-row justify-between">
        <FileInput
          accept="image/png, image/jpeg"
          onChange={({ detail }) => handleFileChange(detail.value)}
          value={[]}
        >
          Foto auswählen
        </FileInput>
        {uploaded ? (
          <div className="flex flex-row gap-2 text-green-700">
            <IconCheck /> Bereits Hochgeladen
          </div>
        ) : easyFile ? (
          <Button variant="primary" onClick={handleUpload}>
            Hochladen
          </Button>
        ) : null}
      </div>
      {easyFile && (
        <>
          <div className="relative w-full h-[200px] bg-[#333] sm:h-[400px] mt-2">
            <Cropper
              image={
                easyFile === imageKey
                  ? `https://${process.env.REACT_APP_UPLOAD_BUCKET}.s3.eu-central-1.amazonaws.com/${imageKey}`
                  : easyFile
              }
              crop={crop}
              aspect={4 / 4}
              zoom={zoom}
              onCropChange={onCropAreaChange}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
            {uploading && (
              <div className="absulte flex justify-center items-center w-full h-full top-0 left-0 right-0 bottom-0 bg-gray-800 opacity-85 z-50">
                <IconLoader className="text-white animate-spin" />
              </div>
            )}
          </div>
          {uploaded && (
            <div className="flex flex-1 justify-end mt-2">
              <Button variant="primary" onClick={handleDeleteImage}>
                Bild Löschen
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FileUpload;
